import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { coverUrl } from "@/lib/api";
import { useI18n } from "@/context/I18n";
import { CassetteTape, ChevronRight, CloudRain, Coffee, Disc3, Dumbbell, Globe, Heart, History, Loader2, Mic2, Music2, Palmtree, PartyPopper, Target, Zap } from "lucide-react";

// Bright on purpose. A mood is a feeling before it is a filter, so the colour
// leads — and what sits under it says what the mood is, rather than showing
// four album sleeves that happened to be filed under it. A Beethoven portrait
// behind "Kids" told a listener nothing.
const MOOD_ICONS = {
  chill: Coffee,
  party: PartyPopper,
  focus: Target,
  workout: Dumbbell,
  romance: Heart,
  sad: CloudRain,
  energy: Zap,
  lofi: CassetteTape,
  latin: Palmtree,
  hiphop: Mic2,
  throwback: History,
  world: Globe,
};

const MOOD_STYLES = {
  chill:     "from-teal-300 to-cyan-500",
  party:     "from-fuchsia-400 to-rose-500",
  focus:     "from-indigo-300 to-blue-500",
  workout:   "from-orange-300 to-red-500",
  romance:   "from-rose-300 to-pink-500",
  sad:       "from-slate-300 to-slate-500",
  energy:    "from-amber-300 to-orange-500",
  lofi:      "from-violet-300 to-purple-500",
  latin:     "from-yellow-300 to-orange-400",
  hiphop:    "from-zinc-300 to-neutral-500",
  throwback: "from-amber-300 to-yellow-500",
  world:     "from-green-300 to-emerald-500",
};

const GENRE_GRADIENTS = [
  "from-[#FF6B85] to-purple-400", "from-emerald-300 to-teal-500",
  "from-sky-300 to-indigo-400", "from-amber-300 to-red-400",
  "from-fuchsia-300 to-rose-400", "from-lime-300 to-green-500",
  "from-cyan-300 to-blue-400", "from-orange-300 to-pink-400",
];

/**
 * The artwork behind a card.
 *
 * The two kinds of card want opposite things. A genre is answered by what it
 * holds, so the sleeves are shown as they are and the colour is only a wash
 * over them. A mood is answered by how it feels, so the colour is the surface
 * and the sleeves sit under it as texture. Both used to be buried under a
 * three-quarter tint and a full-height black gradient, which left them too
 * dark to read as either.
 */
function CardArt({ covers, tint, mood, icon: Icon }) {
  const art = (covers || []).filter(Boolean);
  const veil = mood ? "opacity-25 mix-blend-overlay" : "";

  // A mood shows its own emblem, large and half-there, over the colour.
  if (mood) {
    return (
      <>
        <div className={`absolute inset-0 bg-gradient-to-br ${tint}`} />
        {Icon && (
          <Icon
            className="absolute -right-3 -top-2 w-24 h-24 text-white/25"
            strokeWidth={1.5}
            aria-hidden
          />
        )}
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/55 to-transparent" />
      </>
    );
  }

  return (
    <>
      {art.length >= 4 ? (
        <div className={`absolute inset-0 grid grid-cols-2 grid-rows-2 ${veil}`}>
          {art.slice(0, 4).map((url, i) => (
            <img key={i} src={coverUrl(url, 250)} alt="" className="w-full h-full object-cover" />
          ))}
        </div>
      ) : art.length > 0 ? (
        <img src={coverUrl(art[0], 500)} alt="" className={`absolute inset-0 w-full h-full object-cover ${veil}`} />
      ) : (
        <div className={`absolute inset-0 bg-gradient-to-br ${tint}`} />
      )}

      {art.length > 0 && (
        <div className={`absolute inset-0 bg-gradient-to-br ${tint} opacity-30 mix-blend-soft-light`} />
      )}

      {/* Only the lower half, so the picture keeps its light. */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/65 to-transparent" />
    </>
  );
}

export default function Browse() {
  const { t } = useI18n();
  const [moods, setMoods] = useState(null);
  const [genres, setGenres] = useState(null);

  useEffect(() => {
    api.get("/music/moods").then((r) => setMoods(r.data.moods)).catch(() => setMoods([]));
    api.get("/music/genres").then((r) => setGenres(r.data.genres)).catch(() => setGenres([]));
  }, []);

  const activeMoods = (moods || []).filter((m) => m.track_count > 0);

  return (
    <div className="p-4 md:p-8 pb-6" data-testid="browse-page">
      <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF2A4D]">{t("browse")}</p>
      <h1 className="font-heading text-3xl font-bold tracking-tight mt-1 mb-1">{t("browse")}</h1>
      <p className="text-zinc-400 text-sm mb-6">{t("browse_sub")}</p>

      {/* quick links */}
      <div className="grid grid-cols-2 gap-4 mb-8 max-w-2xl">
        <Link to="/app/artists" data-testid="browse-artists-link" className="flex items-center gap-3 bg-white/5 hover:bg-white/10 rounded-xl p-4 transition-colors">
          <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#FF2A4D] to-fuchsia-700 flex items-center justify-center"><Mic2 className="w-5 h-5" /></div>
          <div className="flex-1"><p className="font-semibold text-sm">{t("all_artists")}</p></div>
          <ChevronRight className="w-4 h-4 text-zinc-500" />
        </Link>
        <Link to="/app/albums" data-testid="browse-albums-link" className="flex items-center gap-3 bg-white/5 hover:bg-white/10 rounded-xl p-4 transition-colors">
          <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-sky-500 to-indigo-800 flex items-center justify-center"><Disc3 className="w-5 h-5" /></div>
          <div className="flex-1"><p className="font-semibold text-sm">{t("all_albums")}</p></div>
          <ChevronRight className="w-4 h-4 text-zinc-500" />
        </Link>
      </div>

      {/* moods */}
      {moods === null ? (
        <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>
      ) : activeMoods.length > 0 && (
        <section className="mb-10">
          <h2 className="font-heading text-xl font-bold mb-3">{t("moods")}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {activeMoods.map((m) => (
              <Link key={m.name} to={`/app/mood/${m.name}`} data-testid={`mood-card-${m.name}`}
                className="relative overflow-hidden rounded-xl aspect-[3/2] hover:scale-[1.02] transition-transform">
                <CardArt tint={MOOD_STYLES[m.name] || "from-zinc-400 to-zinc-600"} icon={MOOD_ICONS[m.name]} mood />
                <span className="absolute bottom-4 left-4 right-4 font-heading text-lg font-bold truncate [text-shadow:0_2px_8px_rgba(0,0,0,.85)]">{t(`mood_${m.name}`)}</span>
                <span className="absolute top-3 right-3 text-xs font-semibold bg-black/45 backdrop-blur-sm rounded-full px-2 py-0.5">{m.track_count}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* genres */}
      <section>
        <h2 className="font-heading text-xl font-bold mb-3">{t("genres")}</h2>
        {genres === null ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>
        ) : genres.length === 0 ? (
          <p className="text-zinc-500 text-sm py-6">{t("no_genres")}</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {genres.map((g, i) => (
              <Link key={g.name} to={`/app/genre/${encodeURIComponent(g.name)}`} data-testid={`genre-card-${g.name}`}
                className="relative overflow-hidden rounded-xl aspect-[3/2] hover:scale-[1.02] transition-transform">
                <CardArt covers={g.covers} tint={GENRE_GRADIENTS[i % GENRE_GRADIENTS.length]} />
                {!(g.covers || []).length && <Music2 className="absolute top-4 left-4 w-5 h-5 opacity-70" />}
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="font-heading text-lg font-bold leading-tight truncate [text-shadow:0_2px_8px_rgba(0,0,0,.85)]">{g.name}</p>
                  <p className="text-xs opacity-90">
                    {g.track_count} {g.track_count === 1 ? t("track_one") : t("tracks")}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
